import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FormikTouched } from 'formik';
import moment from 'moment';

import usePostACHWithdraw from '../../../hooks/useApi/usePostACHWithdraw';
import usePostACHWithdrawSchedule from '../../../hooks/useApi/usePostACHWithdrawSchedule';
import usePostEftWithdraw from '../../../hooks/useApi/usePostEftWithdraw';
import usePostScheduledEftWithdraw from '../../../hooks/useApi/usePostScheduledEftWithdraw';
import usePostWithdrawMyAccount from '../../../hooks/useApi/usePostWithdrawMyAccount';
import usePostWithdrawMyAccountSchedule from '../../../hooks/useApi/usePostWithdrawMyAccountSchedule';
import usePostRtrWithdraw from '../../../hooks/useApi/usePostRtrWithdraw';
import { useNavMenu } from '../../../providers/NavMenu.provider';
import { WithdrawAccountHandler, FormData } from './types';
import WithdrawAccountForm from './WithdrawAccountForm';

import { PendingDataProvider, usePendingData } from '../../../providers/PendingData.provider';

import useFetchClientBankAccounts from '../../../hooks/useApi/useFetchClientBankAccounts';
import { useClientData } from '../../../providers/ClientData.provider';

import { useOpenModule } from '../../../providers/OpenModule.provider';
import WithdrawAccountDialogueContext, { WithdrawAccountCtx } from './Dialogue/WithdrawAccountContext';
import { DialogueHandlerProps, WithdrawAccountDialogueHandler } from './Dialogue/types';

import useFetchDefaultBankAccount from '@/hooks/useApi/useFetchDefaultBankAccount';
import WithdrawAccountDialogue from './Dialogue/WithdrawAccountDialogue';
import useFetchClientAccountList from '@/hooks/useApi/useFetchClientAccountList';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

type TokenProps = {
  token: string;
  cardToken: string;
  type: string;
  currency: string;
  interacID: string;
  account?: {
    RecipientPhoneNumber?: string;
    RecipientName?: string;
    RecipientEmailAddress?: string;
    Question?: string;
    Answer?: string;
    ClientReferenceNumber?: string;
  };
};

const { Box, Title } = vp;

type FC = FunctionComponent;

const WithdrawAccountContainer: FC = () => {
  const reviewRef = useRef<HTMLDivElement>(null);
  const { state } = useLocation<TokenProps>();
  const typeProp = state?.type;
  const currencyProp = state?.currency;
  const { userSettings } = useUserSettingsProvider();

  const [, , , fetchClientAccounts] = useFetchClientAccountList({});
  const [, , , fetchBankAccounts] = useFetchClientBankAccounts({});
  const {
    response: { ClientAccountsEnabled, Country, AccountID },
  } = useClientData();
  const [defaultBankAccountResponse, getDefaultBankAccount] = useFetchDefaultBankAccount({});

  const selectedCurrency = currencyProp !== undefined ? currencyProp : Country === 'US' ? 'USD' : 'CAD';
  const selectedPaymentMethod = typeProp !== undefined ? typeProp : 'Bank Account';

  const [formData, setFormData] = useState<FormData>({
    Currency: selectedCurrency,
    PaymentMethod: selectedPaymentMethod === 'Interac' ? 'Interac e-Transfer' : selectedPaymentMethod,
    Account: '',
    Amount: '0.00',
    ScheduleType: 'single',
    TakeFromAccountOn: String(Date.now()),
    Frequency: 'Monthly',
    SemiMonthlyDates: '',
    StartDate: String(Date.now()),
    Ending: 'End Date',
    EndDate: '',
    Description: '',
    MessageForRecipient: '',
    Notes: 'Withdraw My Account',
    EndingAfterPayments: '',
    ClientAccount: '',
    TransactionLabel: '',
    IdentificationNumber: '',
  });
  const clientAccountsEnabled = ClientAccountsEnabled === '1';

  const [formTouched, setFormTouched] = useState<FormikTouched<FormData>>({});
  const { getModuleTitle } = useNavMenu();
  const [clientAccount, setClientAccount] = useState<vp.ClientAccount | undefined>();
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [showSubmitButton] = useState(true);
  const [ValuesPresent, setValuesPresent] = useState(false);
  const [submissionConfirmed, setSubmissionConfirmed] = useState(false);
  const [selectedBank, setSelectedBank] = useState<vp.ClientBankAccountsLists | undefined>();

  useEffect(() => {
    if (clientAccountsEnabled) return;

    getDefaultBankAccount({
      Timezone: userSettings.timezone,
    });
  }, []);

  // This is for non-client accounts (bank accounts)
  useEffect(() => {
    if (clientAccountsEnabled) return;

    const operationalBank = Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount || {}).find(
      (b) => b.Currency === formData.Currency,
    );

    if (!operationalBank) return;

    fetchBankAccounts({
      Token: operationalBank.Token,
      ClientControlled: true,
      Timezone: userSettings.timezone,
    }).then((res) => {
      if (!res.Success) throw new Error(res.ErrorMessage);

      const bank = res.BankAccounts[0];

      setSelectedBank(bank);

      setFormData((prev) => ({
        ...prev,
        Account: `${bank.Currency} ${bank.AccountHolderName} ${vp.getMaskedNumber(bank.AccountNumber)}`,
      }));
    });
  }, [defaultBankAccountResponse, formData.Currency]);

  // This is for client accounts
  useEffect(() => {
    if (!clientAccountsEnabled) return;

    init();

    async function init() {
      const clientAccountsRes = await fetchClientAccounts({
        ClientType: '',
      });

      if (!clientAccountsRes.Success) throw new Error(clientAccountsRes.ErrorMessage);

      const primaryClientAccount = Object.values(clientAccountsRes.ClientAccounts || {}).find(
        (obj) => obj.IsPrimaryAccount === '1',
      );

      if (!primaryClientAccount) return;

      setClientAccount(primaryClientAccount);

      setFormData((prev) => ({
        ...prev,
        ClientAccount: primaryClientAccount.ClientAccountID,
      }));

      const bankAccountsRes = await fetchBankAccounts({
        ClientAccountID: primaryClientAccount.ClientAccountID,
        Timezone: userSettings.timezone,
      });

      if (!bankAccountsRes.Success) throw new Error(bankAccountsRes.ErrorMessage);

      const defaultBankAccount = Object.values(bankAccountsRes.BankAccounts || {}).find((obj) => obj.IsDefault === '1');

      if (!defaultBankAccount) {
        showValidationError(
          `There is no default bank account set up for client account ID: ${primaryClientAccount.ClientAccountID}`,
        );
      }

      setSelectedBank(defaultBankAccount);
    }
  }, []);

  const { setSubNavActiveModule } = useNavMenu();
  const { setOpenModule } = useOpenModule();

  const { reset } = usePendingData();
  const history = useHistory();

  const [eftWithdrawResponse] = usePostEftWithdraw({});
  const [scheduledEftWithdrawResponse] = usePostScheduledEftWithdraw({});
  const [WithdrawMyAccountResponse, postWithdrawMyAccount] = usePostWithdrawMyAccount({});
  const [withdrawMyAccountScheduleResponse, postWithdrawMyAccountSchedule] = usePostWithdrawMyAccountSchedule({});

  const [ACHWithdrawResponse] = usePostACHWithdraw({});
  const [ACHWithdrawScheduleResponse] = usePostACHWithdrawSchedule({});

  const [shouldWithdrawACHSchedule, setShouldWithdrawACHSchedule] = useState(false);
  const [shouldWithdrawACH, setShouldWithdrawACH] = useState(false);

  const [shouldWithdrawMyAccount, setShouldWithdrawMyAccount] = useState(false);
  const [shouldWithdrawEft, setShouldWithdrawEft] = useState(false);

  const [shouldWithdrawMyAccountSchedule, setShouldWithdrawMyAccountSchedule] = useState(false);
  const [shouldWithdrawScheduledEft, setShouldWithdrawScheduledEft] = useState(false);
  const [showWithdrawAccountErrorAlert, setShowWithdrawAccountErrorAlert] = useState(false);
  const [withdrawAccountError, setWithdrawAccountError] = useState('');
  const [shouldWithdrawRtr, setShouldWithdrawRtr] = useState(false);
  const [rtrWithdrawResponse, postRtrWithdraw] = usePostRtrWithdraw({});

  const [common, setCommon] = useState({
    PortalUser: '',
    Amount: 0,
    FinancialInstitutionNumber: '',
    BranchTransitNumber: '',
    AccountNumber: '',
    ABARoutingNumber: '',
    FirstName: '',
    LastName: '',
    CompanyName: '',
    PhoneNumber: '',
    Address1: '',
    City: '',
    Province: '',
    Country: '',
    Token: '',
    PostalCode: '',
    ClientReferenceNumber: '',
    FlinksAccountID: '',
    FlinksLoginID: '',
    TransactionLabel: '',
  });

  const [, setBankDetail] = useState({
    PlaidAccountID: '',
    AccountHolderName: '',
    BankId: '',
    InstitutionName: '',
    Currency: '',
    EmailAddress: '',
    InveriteRequestGUID: '',
  });

  const WithdrawAccount: WithdrawAccountCtx = {
    showWithdrawAccountErrorAlert,
    withdrawAccountError,
  };
  useEffect(() => {
    if (!selectedBank) return;

    const nameParts = selectedBank?.AccountHolderName?.trim().split(' ') || '';
    const isCompanyName = (nameParts.length > 2 || nameParts.length < 2) && formData?.PaymentMethod !== 'VoPay Instant';
    setCommon({
      PortalUser: AccountID || '',
      Amount: Number(formData.Amount?.replace(/,/g, '')) || 0,
      FinancialInstitutionNumber: clientAccountsEnabled ? '' : selectedBank.FinancialInstitutionNumber || '',
      BranchTransitNumber: clientAccountsEnabled ? '' : selectedBank.BranchTransitNumber || '',
      ABARoutingNumber: clientAccountsEnabled ? '' : selectedBank.ABARoutingNumber || '',
      AccountNumber: clientAccountsEnabled ? '' : selectedBank.AccountNumber || '',
      FirstName: !isCompanyName ? selectedBank.AccountHolderName.split(' ')[0] : '',
      LastName: !isCompanyName ? selectedBank.AccountHolderName.split(' ')[1] : ' ',
      CompanyName: isCompanyName ? selectedBank.AccountHolderName : '',
      PhoneNumber: vp.NumbersOnly(selectedBank.Phone || ''),
      Address1: selectedBank.Address || '',
      City: selectedBank.City || '',
      Province: selectedBank['Province/State'] || '',
      Country: Country || '',
      Token: clientAccountsEnabled ? '' : selectedBank?.Token || '',
      PostalCode: selectedBank['PostalCode/ZipCode'] || '',
      ClientReferenceNumber: selectedBank.ClientReferenceNumber || '',
      FlinksAccountID: clientAccountsEnabled ? '' : selectedBank.FlinksAccountID || '',
      FlinksLoginID: clientAccountsEnabled ? '' : selectedBank.FlinksLoginID || '',
      TransactionLabel: formData.TransactionLabel || '',
    });

    setBankDetail({
      PlaidAccountID: clientAccountsEnabled ? '' : selectedBank.PlaidAccountID,
      AccountHolderName: selectedBank.AccountHolderName,
      BankId: selectedBank.BankID,
      InstitutionName: selectedBank.InstitutionName,
      Currency: selectedBank.Currency,
      EmailAddress: selectedBank.EmailAddress,
      InveriteRequestGUID: selectedBank.InveriteRequestGUID,
    });
  }, [selectedBank, formData]);

  const handlersForm = {
    handleWithdrawAccountDialogue: (handlerProps: DialogueHandlerProps<WithdrawAccountDialogueHandler>) => {
      const { type, state: stateDialogue, action = 'default' } = handlerProps;
      const dialogueHandlers: WithdrawAccountDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowWithdrawAccountErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][stateDialogue];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },

    onWithdrawAccount: (_values: FormData) => {
      if (
        formData.PaymentMethod === 'Bank Account' &&
        formData.ScheduleType === 'single' &&
        formData.TakeFromAccountOn &&
        vp.IsTodayDate(moment(Number(formData.TakeFromAccountOn)).format('YYYY-MM-DD'))
      ) {
        setShouldWithdrawMyAccount(true);
        postWithdrawMyAccount({
          ...common,
          Currency: formData.Currency || '',
          Notes: formData.Notes || '',
          ClientAccountID: clientAccountsEnabled ? formData.ClientAccount : '',
        });
        reset();
      } else if (
        (formData.PaymentMethod === 'Bank Account' && formData.ScheduleType === 'recurring') ||
        (formData.TakeFromAccountOn && !vp.IsTodayDate(moment(Number(formData.TakeFromAccountOn)).format('YYYY-MM-DD')))
      ) {
        setShouldWithdrawMyAccountSchedule(true);
        postWithdrawMyAccountSchedule({
          Amount: common.Amount,
          Frequency: formData.ScheduleType || '',
          NameOfFrequency: formData.Frequency || '',
          SemiMonthlyFrequencyType: formData.SemiMonthlyDates || '',
          ScheduleStartDate:
            formData.ScheduleType === 'recurring'
              ? moment(Number(formData.StartDate)).format('YYYY-MM-DD') || ''
              : moment(Number(formData.TakeFromAccountOn)).format('YYYY-MM-DD') || '',
          ScheduleEndDate:
            formData.Ending === 'End Date' && formData.ScheduleType === 'recurring'
              ? moment(Number(formData.EndDate)).format('YYYY-MM-DD') || ''
              : '',
          Currency: formData.Currency || '',
          EndingAfterPayments: formData.Ending === 'Payments' ? formData.EndingAfterPayments || '' : '',
          Description: formData.Description || '',
        });
        reset();
      } else if (formData.PaymentMethod === 'VoPay Instant') {
        setShouldWithdrawRtr(true);
        postRtrWithdraw({
          ...common,
          ClientAccountID: clientAccountsEnabled ? formData.ClientAccount : '',
        });
        reset();
      }
    },
  };

  const handlers: WithdrawAccountHandler = {
    onWithdrawAccount: (_values) => {
      if (isUnderReview) {
        setFormData(_values);
        handlersForm.onWithdrawAccount(_values);
      } else {
        setFormData(_values);
        setIsUnderReview(true);
      }
    },
    onWithdrawChange: (_values, _errors, touched) => {
      setFormTouched(touched);
      setFormData(_values);
    },
  };

  useEffect(() => {
    if (shouldWithdrawEft) {
      const { Success, ErrorMessage } = eftWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/EFTWithdrawal/${eftWithdrawResponse.TransactionID}/${formData.Currency}/`,
          state: {
            transactionId: eftWithdrawResponse.TransactionID,
            activeModule: 'withdrawMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowWithdrawAccountErrorAlert(true);
        setWithdrawAccountError(ErrorMessage);
      }
      setShouldWithdrawEft(false);
    }
    if (shouldWithdrawMyAccount && WithdrawMyAccountResponse) {
      const { Success, ErrorMessage } = WithdrawMyAccountResponse;

      if (Success === true) {
        if (Country === 'US') {
          history.push({
            pathname: `/transactions-history/details/ACHWithdrawal/${WithdrawMyAccountResponse.TransactionID}/${formData.Currency}/`,
            state: {
              transactionId: WithdrawMyAccountResponse.TransactionID,
              activeModule: 'withdrawMyAccount',
              hasNewTransactionButton: true,
            },
          });
        } else {
          history.push({
            pathname: `/transactions-history/details/EFTWithdrawal/${WithdrawMyAccountResponse.TransactionID}/${formData.Currency}/`,
            state: {
              transactionId: WithdrawMyAccountResponse.TransactionID,
              activeModule: 'withdrawMyAccount',
              hasNewTransactionButton: true,
            },
          });
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowWithdrawAccountErrorAlert(true);
        setWithdrawAccountError(ErrorMessage);
      }
      setShouldWithdrawEft(false);
    }

    if (shouldWithdrawScheduledEft) {
      const { Success, ErrorMessage } = scheduledEftWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${scheduledEftWithdrawResponse.ScheduledTransactionID}/`,
          state: {
            transactionId: scheduledEftWithdrawResponse.ScheduledTransactionID,
            activeModule: 'withdrawMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('transactions');
      } else if (Success === false) {
        setShowWithdrawAccountErrorAlert(true);
        setWithdrawAccountError(ErrorMessage);
      }
      setShouldWithdrawScheduledEft(false);
    }
    if (shouldWithdrawMyAccountSchedule === true && withdrawMyAccountScheduleResponse) {
      const { Success, ErrorMessage } = withdrawMyAccountScheduleResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${withdrawMyAccountScheduleResponse?.ScheduledTransactionID}/`,
          state: {
            transactionId: withdrawMyAccountScheduleResponse.ScheduledTransactionID,
            activeModule: 'withdrawMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('transactions');
      } else if (Success === false) {
        setShowWithdrawAccountErrorAlert(true);
        setWithdrawAccountError(ErrorMessage);
      }
      setShouldWithdrawMyAccountSchedule(false);
    }
    if (shouldWithdrawACHSchedule === true && ACHWithdrawScheduleResponse) {
      const { Success, ErrorMessage } = ACHWithdrawScheduleResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${ACHWithdrawScheduleResponse?.ScheduledTransactionID}/`,
          state: {
            transactionId: ACHWithdrawScheduleResponse.ScheduledTransactionID,
            activeModule: 'withdrawMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('transactions');
      } else if (Success === false) {
        setShowWithdrawAccountErrorAlert(true);
        setWithdrawAccountError(ErrorMessage);
      }
      setShouldWithdrawACHSchedule(false);
    }
    if (ACHWithdrawResponse && shouldWithdrawACH) {
      const { Success, ErrorMessage } = ACHWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/ACHWithdrawal/${ACHWithdrawResponse.TransactionID}/${formData.Currency}/`,
          state: {
            transactionId: ACHWithdrawResponse.TransactionID,
            activeModule: 'withdrawMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowWithdrawAccountErrorAlert(true);
        setWithdrawAccountError(ErrorMessage);
      }

      setShouldWithdrawACH(false);
    }
    if (shouldWithdrawRtr) {
      const { Success, ErrorMessage } = rtrWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/VoPayInstantWithdraw/${rtrWithdrawResponse.TransactionID}/${formData.Currency}/`,
          state: {
            transactionId: rtrWithdrawResponse.TransactionID,
            activeModule: 'withdrawMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('transactions');
      } else if (Success === false) {
        setShowWithdrawAccountErrorAlert(true);
        setWithdrawAccountError(ErrorMessage);
      }
      setShouldWithdrawRtr(false);
    }
  }, [
    eftWithdrawResponse,
    scheduledEftWithdrawResponse,
    rtrWithdrawResponse,
    WithdrawMyAccountResponse,
    withdrawMyAccountScheduleResponse,
    ACHWithdrawResponse,
    ACHWithdrawScheduleResponse,
  ]);
  useEffect(() => {
    if (submissionConfirmed && ValuesPresent) {
      setSubmissionConfirmed(false);
      setValuesPresent(false);
    }
  }, [submissionConfirmed, ValuesPresent]);

  useEffect(() => {
    if (isUnderReview) {
      if (reviewRef !== null && reviewRef.current !== null) {
        reviewRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [isUnderReview]);

  return (
    <div ref={reviewRef}>
      <Box mb="md" jc="sb" flx="col" gap="lg">
        <Box mb="md" jc="sb">
          {isUnderReview ? <Title title="Review and Submit" /> : <Title title={getModuleTitle()} />}
        </Box>
        <PendingDataProvider>
          <WithdrawAccountDialogueContext.Provider value={WithdrawAccount}>
            <WithdrawAccountForm
              handlers={handlers}
              clientAccount={clientAccount}
              defaultValues={formData}
              defaultTouches={formTouched}
              isUnderReview={isUnderReview}
              setIsUnderReview={setIsUnderReview}
              showSubmitButton={showSubmitButton}
              hasBankAccount={!!selectedBank}
            />
            <WithdrawAccountDialogue handleDialogue={handlersForm.handleWithdrawAccountDialogue} />
          </WithdrawAccountDialogueContext.Provider>
        </PendingDataProvider>
      </Box>
    </div>
  );
  function showValidationError(ErrorMessage: string) {
    setShowWithdrawAccountErrorAlert(true);
    setWithdrawAccountError(ErrorMessage);
  }
};

export default WithdrawAccountContainer;
